// @mui
import {
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  alpha,
  styled,
} from '@mui/material';
// config
import { ICON, NAV } from '../../../config-global';
//
import { DARK_TEXT } from 'src/theme/palette';
import { NavItemProps } from '../types';
// ----------------------------------------------------------------------

type StyledItemProps = Omit<NavItemProps, 'item'> & {
  caption?: boolean;
  disabled?: boolean;
};

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'caption',
})<StyledItemProps>(({ active, disabled, depth, caption, theme }) => {
  const isLight = theme.palette.mode === 'light';

  const subItem = depth !== 1;

  const activeStyle = {
    color: theme.palette.primary.main,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity,
    ),
    ...(!isLight && {
      color: theme.palette.primary.light,
    }),
  };

  // The sidebar is painted with `palette.primary.darker`, so an active sub
  // item coloured `text.primary` was invisible against its own background.
  const activeSubStyle = {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  };

  const hoverStyle = {
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  };

  return {
    position: 'relative',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
    color: DARK_TEXT.primary,
    borderRadius: theme.shape.borderRadius,
    height: NAV.H_DASHBOARD_ITEM,
    transition: theme.transitions.create(['color', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '& .MuiListItemIcon-root': {
      transition: theme.transitions.create('color', {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '&:hover': hoverStyle,
    // Sub item
    ...(subItem && {
      height: NAV.H_DASHBOARD_ITEM_SUB,
      paddingLeft: theme.spacing(3),
      ...(depth > 2 && {
        paddingLeft: theme.spacing(depth + 4),
      }),
      ...(caption && {
        height: NAV.H_DASHBOARD_ITEM,
      }),
    }),
    // Active item
    ...(active && {
      ...activeStyle,
      '&:hover': {
        ...activeStyle,
      },
    }),
    // Active sub item
    ...(subItem &&
      active && {
        ...activeSubStyle,
        '&:hover': {
          ...activeSubStyle,
        },
      }),
    // Disabled
    ...(disabled && {
      '&.Mui-disabled': {
        opacity: 0.64,
      },
    }),
  };
});

// ----------------------------------------------------------------------

/** An open top-level group (e.g. Magazyn): its parent and sub items share one tinted background. */
export const StyledGroup = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ open, theme }) => ({
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shorter,
  }),
  // The same tint as an active item.
  ...(open && {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity,
    ),
  }),
}));

// ----------------------------------------------------------------------

type StyledIconProps = {
  active?: boolean;
  depth?: number;
};

export const StyledIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'depth',
})<StyledIconProps>(({ active, depth, theme }) => {
  const subItem = depth !== 1;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: ICON.NAV_ITEM,
    height: ICON.NAV_ITEM,
    // Active parent item
    ...(active &&
      !subItem && {
        color: theme.palette.primary.main,
      }),
    // Active sub item
    ...(active &&
      subItem && {
        color: theme.palette.primary.main,
      }),
  };
});

// ----------------------------------------------------------------------

type StyledDotIconProps = {
  active?: boolean;
};

export const StyledDotIcon = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledDotIconProps>(({ active, theme }) => ({
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: theme.palette.text.disabled,
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shorter,
  }),
  ...(active && {
    transform: 'scale(2)',
    backgroundColor: theme.palette.primary.main,
  }),
}));

// ----------------------------------------------------------------------

export const StyledSubheader = styled(ListSubheader)(({ theme }) => ({
  ...theme.typography.overline,
  fontSize: 11,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
}));
